// ChecklistItemResources.js
// Canonical resource refs on checklist template / workflow recipe items.

import { createEnumOptionId } from 'utilities/EnumOptions';

const normalizeString = (value) => String(value || '').trim();

/**
 * @param {unknown} resources
 * @returns {Array<object>}
 */
export function normalizeChecklistItemResources(resources) {
  if (!Array.isArray(resources)) return [];

  const result = [];
  const seenIds = new Set();
  const seenNodeKeys = new Set();

  resources.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object') return;

    const nodeKey = normalizeString(raw.nodeKey || raw.key);
    if (!nodeKey || seenNodeKeys.has(nodeKey)) return;

    let id = normalizeString(raw.id);
    if (!id || seenIds.has(id)) {
      id = createEnumOptionId();
    }

    seenIds.add(id);
    seenNodeKeys.add(nodeKey);

    const title = normalizeString(raw.title);
    result.push({
      id,
      nodeKey,
      ...(title ? { title } : {}),
      sortOrder: Number.isFinite(Number(raw.sortOrder)) ? Number(raw.sortOrder) : index,
    });
  });

  return result
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((resource, index) => ({
      ...resource,
      sortOrder: index,
    }));
}

/**
 * @param {object} params
 * @param {string} params.nodeKey
 * @param {string} [params.title]
 * @param {string} [params.id]
 * @returns {object|null}
 */
export function buildChecklistItemResource({
  nodeKey,
  title = '',
  id = '',
} = {}) {
  const nextNodeKey = normalizeString(nodeKey);
  if (!nextNodeKey) return null;

  const nextTitle = normalizeString(title);
  return {
    id: normalizeString(id) || createEnumOptionId(),
    nodeKey: nextNodeKey,
    ...(nextTitle ? { title: nextTitle } : {}),
  };
}

/**
 * Collect unique resource node keys from checklist recipe items.
 *
 * @param {unknown} items
 * @returns {string[]}
 */
export function collectChecklistResourceNodeKeys(items) {
  const keys = [];
  const seen = new Set();

  (Array.isArray(items) ? items : []).forEach((item) => {
    normalizeChecklistItemResources(item?.resources).forEach((resource) => {
      if (seen.has(resource.nodeKey)) return;
      seen.add(resource.nodeKey);
      keys.push(resource.nodeKey);
    });
  });

  return keys;
}
